// src/pages/lab/labOrders/components/LabOrderDrawer.tsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import { Button, Modal } from "antd";
import { SaveOutlined, PlusOutlined, CheckCircleOutlined } from "@ant-design/icons";

// Import API
import { fetchTestCategories } from "../../../../api/healthcare/Testing/TestCategories";
import { fetchTestTypesByCategory } from "../../../../api/healthcare/Testing/TestTypes";
import { getLabOrder, createLabOrder, updateLabOrder } from "../../../../api/labiq/LabOrdersApi";

//Types
import { Company } from "../../../../types/admin/CompanyTypes";
import { LabOrder, parseTestType } from "../../../../types/lab/LabOrderTypes";
import { TestCategory, TestType } from "../../../../types/healthcare/TestTypes";

// Components for the drawer
import OrderDetails from "./orderDrawer/OrderDetails";
import OrderItems from "./orderDrawer/OrderItems";
import OrderShipping from "./orderDrawer/OrderShipping";
import { DetailDrawer } from "../../../../components/shared";

//Utils and hooks
import { useNotifications } from "../../../../context/NotificationContext";

interface LabOrderDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	lab_order_id: number;
}

const LabOrderDrawer: React.FC<LabOrderDrawerProps> = ({ isOpen, onClose, lab_order_id }) => {
	const user = getSessionUser();
	const [orderData, setOrderData] = useState<Partial<LabOrder>>({});
	const [testCategories, setTestCategories] = useState<TestCategory[]>([]);
	const [testTypes, setTestTypes] = useState<TestType[]>([]);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [errorMessages, setErrorMessages] = useState<any[]>([]);

	const { showInfo, showError, showSuccess, showWarning } = useNotifications();

	useEffect(() => {
		if (!isOpen) return;

		const initializeData = async () => {
			try {
				if (lab_order_id !== 0) {
					await getOrderDetails();
				} else {

					setOrderData({
						order_number: "",
						doctor_id: 0,
						sample_type: "",
						test_type: JSON.stringify([]),
						status: "pending",
						priority: "normal",
						ordered_at: new Date().toISOString(),
						notes: "",
						order_items: [],
					});
				}
				await fetchTestCategoryList();
			} catch (error) {
				handleError(error);
			}
		};

		initializeData();
	}, [isOpen, lab_order_id]);


	const fetchTestCategoryList = async () => {
		try {
			const response = await fetchTestCategories({
				include_test_types: false,
			});
			if (response.success) {
				setTestCategories(response.data.filter((cat) => cat.is_active));
			}
		} catch (error) {
			handleError(error);
		}
	};

	const fetchTestTypesForCategory = async (categoryValue: string) => {
		try {
			const response = await fetchTestTypesByCategory(categoryValue);
			if (response.success) {
				setTestTypes(response.data.filter((type) => type.is_active));
			}
		} catch (error) {
			handleError(error);
		}
	};

	const getOrderDetails = async () => {
		try {
			const order = await getLabOrder(lab_order_id);

			// Store test_type as JSON string internally for main order
			if (order.test_type) {
				order.test_type = JSON.stringify(parseTestType(order.test_type));
			}

			// Store test_type as JSON string internally for all order items
			if (order.order_items) {
				order.order_items = order.order_items.map((item) => ({
					...item,
					test_type: JSON.stringify(parseTestType(item.test_type)),
				}));
			}

			setOrderData({ ...order });
		} catch (error) {
			handleError(error);
		}
	};

	const updateOrderData = (field: string, value: any) => {
		// If updating test_type and value is an array, stringify it for internal storage
		if (field === "test_type" && Array.isArray(value)) {
			setOrderData((prev) => ({ ...prev, [field]: JSON.stringify(value) }));
		} else {
			setOrderData((prev) => ({ ...prev, [field]: value }));
		}
		autoSave();
	};

	const handleAddOrderItem = () => {
		if (orderData.order_items) {
			const lastIndex = orderData.order_items.length - 1;
			const lastItem = lastIndex >= 0 ? orderData.order_items[lastIndex] : null;

			if (lastItem) {
				const lastItemTestTypes = parseTestType(lastItem.test_type);
				if (!lastItem.sample_type || lastItemTestTypes.length === 0) {
					showWarning("Please fill in Sample Type and Test Type of last item before adding another");
					return;
				}
			}
		}

		setOrderData((prev) => ({
			...prev,
			order_items: [
				...(prev.order_items || []),
				{
					id: Date.now(),
					order_id: lab_order_id,
					sample_type: orderData.sample_type || "",
					test_category: orderData.test_category || "",
					test_type: orderData.test_type || JSON.stringify([]),
					status: "pending",
					notes: "",
				},
			],
		}));

		showSuccess("Order item added");
	};

	const prepareOrderDataForSubmission = (data: Partial<LabOrder>) => {
		const preparedData = { ...data };

		// Convert test_type from internal JSON string format to string for backend
		if (preparedData.test_type) {
			const testTypes = parseTestType(preparedData.test_type);
			preparedData.test_type = JSON.stringify(testTypes) as any;
		}

		// Convert test_type from internal JSON string format to string for all order items
		if (preparedData.order_items) {
			preparedData.order_items = preparedData.order_items.map((item) => ({
				...item,
				test_type: JSON.stringify(parseTestType(item.test_type)) as any,
			}));
		}

		return preparedData;
	};

	const handleSave = async () => {
		if (lab_order_id === 0) {
			setIsSubmitting(true);
			try {
				const preparedData = prepareOrderDataForSubmission(orderData);
				console.log("Creating order with data:", preparedData); // Debug log
				await createLabOrder(preparedData as LabOrder);
				showSuccess("Lab Order Created");
				onClose();
			} catch (error) {
				handleError(error);
			} finally {
				setIsSubmitting(false);
			}
		} else {
			await autoSave();
		}
	};

	const autoSave = async () => {
		if (isSubmitting || lab_order_id === 0 || orderData.status === "completed") return;

		setIsSubmitting(true);
		try {
			const preparedData = prepareOrderDataForSubmission(orderData);
			console.log("Updating order with data:", preparedData); // Debug log
			await updateLabOrder(lab_order_id, preparedData as LabOrder);
		} catch (error) {
			handleError(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const completeOrderClick = async () => {
		setIsSubmitting(true);
		try {
			let currentOrderId = lab_order_id;
			if (lab_order_id === 0) {
				const preparedData = prepareOrderDataForSubmission(orderData);
				const createdOrder = await createLabOrder(preparedData as LabOrder);
				currentOrderId = createdOrder.id;
				setOrderData((prev) => ({ ...prev, id: currentOrderId }));
			} else {
				const preparedData = prepareOrderDataForSubmission(orderData);
				await updateLabOrder(lab_order_id, preparedData as LabOrder);
			}

			const updatedOrder = {
				...orderData,
				status: "completed",
				completed_at: new Date().toISOString(),
			};
			const preparedData = prepareOrderDataForSubmission(updatedOrder);
			await updateLabOrder(currentOrderId, preparedData as LabOrder);

			await getOrderDetails();
			onClose();
			showSuccess("Order completed successfully");
		} catch (error) {
			handleError(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const confirmOrderCompletion = () => {
		Modal.confirm({
			title: "Confirmation",
			content: "Are you sure you want to mark this order as completed? This action cannot be undone.",
			okText: "Yes",
			cancelText: "No",
			onOk: () => completeOrderClick(),
		});
	};

	const handleError = (error: unknown) => {
		if (axios.isAxiosError(error)) {
			if (error.response?.data?.errors) {
				const errorMessages = Object.entries(error.response.data.errors).flatMap(([_, messages]) => messages);
				setErrorMessages(errorMessages);
			} else if (error.response?.data?.message) {
				setErrorMessages([error.response.data.message]);
			} else {
				setErrorMessages(["An unknown error occurred."]);
			}
		} else if (error instanceof Error) {
			setErrorMessages([error.message || "An unknown error occurred."]);
		} else {
			setErrorMessages(["An unknown error occurred."]);
		}
	};

	const tabItems = [
		{
			key: "1",
			label: "Order Details",
			children: (
				<OrderDetails orderData={orderData} updateOrderData={updateOrderData} setErrorMessages={setErrorMessages} testCategories={testCategories} testTypes={testTypes} onCategoryChange={fetchTestTypesForCategory} />
			),
		},
		{
			key: "2",
			label: "Order Items",
			children: <OrderItems lab_order_id={lab_order_id} orderData={orderData} setOrderData={setOrderData} testCategories={testCategories} testTypes={testTypes} onCategoryChange={fetchTestTypesForCategory} autoSave={autoSave} />,
		},
		{
			key: "3",
			label: "Shipping",
			children: <OrderShipping orderData={orderData} updateOrderData={updateOrderData} />,
		},
	];

	return (
		<DetailDrawer
			open={isOpen}
			onClose={onClose}
			width="85vw"
			title={
				lab_order_id === 0
					? "Create Lab Order"
					: `${orderData.status === "completed" ? "Viewing" : "Editing"} Order ${orderData.order_number}`
			}
			subtitle={lab_order_id !== 0 ? user.username : undefined}
			headerActions={
				<Button data-testid="orders-save-btn" type="primary" icon={<SaveOutlined />} loading={isSubmitting} onClick={handleSave} size="small">
					Save
				</Button>
			}
			tabs={tabItems}
			errorMessages={errorMessages.map(String)}
			onClearErrors={() => setErrorMessages([])}
			footer={
				<>
					{orderData.status !== "completed" && (
						<Button type="dashed" icon={<PlusOutlined />} onClick={handleAddOrderItem} disabled={orderData.status === "completed"} size="small">
							Add Order Item
						</Button>
					)}
					{orderData.status === "processing" && (
						<Button type="primary" icon={<CheckCircleOutlined />} onClick={confirmOrderCompletion} loading={isSubmitting} size="small">
							Complete Order
						</Button>
					)}
				</>
			}
		/>
	);
};

export default LabOrderDrawer;
